import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { downloadCSV } from '@/lib/csv';
import {
  DEFAULT_ALERT_CONFIG,
  errorFingerprint,
  fetchErrorAlerts,
  isNoiseError,
  loadErrorAlertConfig,
  type ErrorAlertConfig,
  type PlatformErrorAlert,
} from '@/lib/platformLogs';
import ErrorAlertsPanel from './ErrorAlertsPanel';
import { openErrorPage, prettyPage } from './logUtils';

interface PlatformErrorLog {
  id: number;
  level: string;
  source: string | null;
  page: string | null;
  category: string | null;
  message: string;
  details: Record<string, unknown> | null;
  user_id: string | null;
  user_label: string | null;
  user_agent: string | null;
  url: string | null;
  resolved: boolean;
  created_at: string;
}

interface Incident {
  key: string;
  page: string;
  level: string;
  category: string | null;
  message: string;
  occurrences: number;
  firstAt: string;
  lastAt: string;
  resolved: boolean;
  latest: PlatformErrorLog;
  logs: PlatformErrorLog[];
}

type Period = '24h' | '7d' | '30d' | 'all';
type SortMode = 'recent' | 'frequent';

const PERIOD_LABELS: Record<Period, string> = {
  '24h': '24 heures',
  '7d': '7 jours',
  '30d': '30 jours',
  all: 'Tout',
};

const LEVEL_CONFIG: Record<string, { label: string; icon: string; badge: string; dot: string }> = {
  error: { label: 'Erreur', icon: 'ri-error-warning-line', badge: 'bg-red-50 text-red-600', dot: 'bg-red-500' },
  warn: { label: 'Alerte', icon: 'ri-alert-line', badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  info: { label: 'Info', icon: 'ri-information-line', badge: 'bg-secondary-100 text-secondary-800', dot: 'bg-secondary-400' },
};

const LEVEL_RANK: Record<string, number> = { error: 3, warn: 2, info: 1 };

function sinceISO(period: Period): string | null {
  if (period === 'all') return null;
  const hours = period === '24h' ? 24 : period === '7d' ? 24 * 7 : 24 * 30;
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function relative(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

export default function PlatformErrorsTab() {
  const [logs, setLogs] = useState<PlatformErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('7d');
  const [level, setLevel] = useState<'all' | 'error' | 'warn'>('all');
  const [pageFilter, setPageFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  const [alerts, setAlerts] = useState<PlatformErrorAlert[]>([]);
  const [alertConfig, setAlertConfig] = useState<ErrorAlertConfig>(DEFAULT_ALERT_CONFIG);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let query = supabase
        .from('platform_error_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1200);
      const since = sinceISO(period);
      if (since) query = query.gte('created_at', since);
      const { data, error } = await query;
      if (error) throw error;
      setLogs((data as PlatformErrorLog[]) || []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Impossible de charger le journal');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const refreshAlerts = useCallback(async () => {
    try {
      const [rows, config] = await Promise.all([fetchErrorAlerts(true, 20), loadErrorAlertConfig()]);
      setAlerts(rows);
      setAlertConfig(config);
    } catch {
      /* les alertes ne doivent jamais bloquer le journal */
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    void refreshAlerts();
    const onAlert = () => void refreshAlerts();
    window.addEventListener('zifek:platform-alert', onAlert);
    const timer = window.setInterval(() => void refreshAlerts(), 60000);
    return () => {
      window.removeEventListener('zifek:platform-alert', onAlert);
      window.clearInterval(timer);
    };
  }, [refreshAlerts]);

  // 🧹 Balises analytics, requêtes annulées, HTTP 0 : ce n'est pas une panne.
  const visibleLogs = useMemo(() => logs.filter((l) => !isNoiseError(l.message)), [logs]);
  const noiseCount = logs.length - visibleLogs.length;

  // ⚠️ Regroupement : les erreurs identiques (même page + même catégorie +
  // même message normalisé) forment un seul incident avec son compteur.
  const incidents = useMemo<Incident[]>(() => {
    const map = new Map<string, Incident>();
    visibleLogs.forEach((log) => {
      const key = errorFingerprint(log);
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          key,
          page: log.page || '/',
          level: log.level,
          category: log.category,
          message: log.message,
          occurrences: 1,
          firstAt: log.created_at,
          lastAt: log.created_at,
          resolved: !!log.resolved,
          latest: log,
          logs: [log],
        });
        return;
      }
      existing.occurrences += 1;
      existing.logs.push(log);
      existing.resolved = existing.resolved && !!log.resolved;
      if (new Date(log.created_at).getTime() > new Date(existing.lastAt).getTime()) {
        existing.lastAt = log.created_at;
        existing.latest = log;
        existing.message = log.message;
      }
      if (new Date(log.created_at).getTime() < new Date(existing.firstAt).getTime()) {
        existing.firstAt = log.created_at;
      }
      if ((LEVEL_RANK[log.level] || 0) > (LEVEL_RANK[existing.level] || 0)) {
        existing.level = log.level;
      }
      if (!existing.category && log.category) existing.category = log.category;
    });
    const list = Array.from(map.values());
    list.forEach((incident) => {
      incident.logs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    });
    return list;
  }, [visibleLogs]);

  const stats = useMemo(() => {
    const now = Date.now();
    const last24h = visibleLogs.filter((l) => now - new Date(l.created_at).getTime() < 86400000);
    return {
      incidents: incidents.length,
      occurrences: visibleLogs.length,
      last24h: last24h.length,
      errors: visibleLogs.filter((l) => l.level === 'error').length,
      pages: new Set(visibleLogs.map((l) => l.page || '/')).size,
      unresolved: incidents.filter((i) => !i.resolved).length,
    };
  }, [visibleLogs, incidents]);

  // Classement des pages : on compte les occurrences ET les incidents.
  const byPage = useMemo(() => {
    const map = new Map<string, { occurrences: number; incidents: number; errors: number }>();
    incidents.forEach((incident) => {
      const entry = map.get(incident.page) || { occurrences: 0, incidents: 0, errors: 0 };
      entry.occurrences += incident.occurrences;
      entry.incidents += 1;
      if (incident.level === 'error') entry.errors += incident.occurrences;
      map.set(incident.page, entry);
    });
    return Array.from(map.entries())
      .map(([page, value]) => ({ page, ...value }))
      .sort((a, b) => b.errors - a.errors || b.occurrences - a.occurrences)
      .slice(0, 8);
  }, [incidents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = incidents.filter((incident) => {
      if (!showResolved && incident.resolved) return false;
      if (level !== 'all' && incident.level !== level) return false;
      if (pageFilter !== 'all' && incident.page !== pageFilter) return false;
      if (q) {
        const haystack = `${incident.message} ${incident.category || ''} ${incident.page} ${incident.logs
          .map((l) => `${l.user_label || ''} ${JSON.stringify(l.details || {})}`)
          .join(' ')}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    return rows.sort((a, b) => {
      if (sortMode === 'frequent') return b.occurrences - a.occurrences || new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime();
      return new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime();
    });
  }, [incidents, level, pageFilter, search, showResolved, sortMode]);

  const toggleResolved = async (incident: Incident) => {
    setWorking(incident.key);
    const target = !incident.resolved;
    try {
      const ids = incident.logs.map((l) => l.id);
      await supabase.from('platform_error_logs').update({ resolved: target }).in('id', ids);
      const idSet = new Set(ids);
      setLogs((prev) => prev.map((l) => (idSet.has(l.id) ? { ...l, resolved: target } : l)));
    } catch {
      /* ignore */
    } finally {
      setWorking(null);
    }
  };

  const copyDetails = (incident: Incident) => {
    const log = incident.latest;
    const text = [
      `Incident : ${prettyPage(incident.page)}`,
      `Occurrences : ${incident.occurrences}`,
      `Première : ${new Date(incident.firstAt).toLocaleString('fr-FR')}`,
      `Dernière : ${new Date(incident.lastAt).toLocaleString('fr-FR')}`,
      `Niveau : ${incident.level}`,
      `Catégorie : ${incident.category || '-'}`,
      `Message : ${incident.message}`,
      `Utilisateur : ${log.user_label || log.user_id || '-'}`,
      `URL : ${log.url || '-'}`,
      `Détails : ${JSON.stringify(log.details || {}, null, 2)}`,
    ].join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const focusPage = (page: string) => {
    setPageFilter(page);
    setExpanded(null);
  };

  /** Export CSV des incidents affichés (filtres appliqués), prêt à transmettre. */
  const exportCsv = () => {
    if (filtered.length === 0) return;
    const headers = [
      'Incident', 'Page', 'Chemin', 'Niveau', 'Categorie', 'Occurrences',
      'Premiere_occurrence', 'Derniere_occurrence', 'Resolu', 'Message',
      'Dernier_utilisateur', 'Derniere_URL', 'Navigateur', 'Contexte_technique',
    ];
    const rows = filtered.map((incident, index) => [
      String(index + 1),
      prettyPage(incident.page),
      incident.page,
      LEVEL_CONFIG[incident.level]?.label || incident.level,
      incident.category || '',
      String(incident.occurrences),
      new Date(incident.firstAt).toISOString(),
      new Date(incident.lastAt).toISOString(),
      incident.resolved ? 'oui' : 'non',
      incident.message,
      incident.latest.user_label || incident.latest.user_id || '',
      incident.latest.url || '',
      incident.latest.user_agent || '',
      JSON.stringify(incident.latest.details || {}, null, 2),
    ]);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadCSV(`zifek-incidents-${stamp}.csv`, headers, rows);
  };

  return (
    <div>
      <ErrorAlertsPanel
        alerts={alerts}
        config={alertConfig}
        onConfigSaved={setAlertConfig}
        onRefresh={() => void refreshAlerts()}
        onFocusPage={focusPage}
      />

      {/* Synthèse */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { icon: 'ri-bug-line', label: 'Incidents', value: stats.incidents, sub: `${stats.occurrences} occurrences regroupées`, color: 'bg-red-50 text-red-600' },
          { icon: 'ri-time-line', label: 'Occurrences 24 h', value: stats.last24h, sub: 'Événements enregistrés', color: 'bg-amber-50 text-amber-700' },
          { icon: 'ri-layout-masonry-line', label: 'Pages touchées', value: stats.pages, sub: 'Pages distinctes concernées', color: 'bg-primary-100 text-primary-700' },
          { icon: 'ri-checkbox-circle-line', label: 'Incidents ouverts', value: stats.unresolved, sub: `${stats.errors} occurrences de niveau erreur`, color: 'bg-secondary-100 text-secondary-800' },
        ].map((card) => (
          <div key={card.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center mb-2`}>
              <i className={`${card.icon} text-sm`}></i>
            </div>
            <div className="text-xl font-bold font-heading text-foreground-950">{card.value.toLocaleString()}</div>
            <div className="text-xs text-foreground-500">{card.label}</div>
            <div className="text-[11px] text-foreground-400 mt-0.5">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Répartition par page */}
      {byPage.length > 0 && (
        <div className="mb-6 rounded-lg border border-background-200/70 bg-background-50 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-foreground-700 flex items-center gap-2">
              <i className="ri-layout-masonry-line text-primary-500"></i>
              Où ça casse le plus
            </h3>
            {pageFilter !== 'all' && (
              <button
                onClick={() => setPageFilter('all')}
                className="text-[11px] text-foreground-500 hover:text-foreground-800 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-close-line"></i>Retirer le filtre
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {byPage.map((item) => (
              <button
                key={item.page}
                onClick={() => setPageFilter(pageFilter === item.page ? 'all' : item.page)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] transition-colors cursor-pointer ${
                  pageFilter === item.page
                    ? 'border-primary-300 bg-primary-50 text-primary-800'
                    : 'border-background-200/70 bg-background-100 text-foreground-700 hover:bg-background-200/60'
                }`}
                title={`${item.page} — ${item.incidents} incident(s), ${item.occurrences} occurrence(s)`}
              >
                <span className="font-medium whitespace-nowrap">{prettyPage(item.page)}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${item.errors > 0 ? 'bg-red-100 text-red-600' : 'bg-secondary-100 text-secondary-800'}`}>
                  {item.occurrences}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                period === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {[
            { key: 'all', label: 'Tout' },
            { key: 'error', label: 'Erreurs' },
            { key: 'warn', label: 'Alertes' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setLevel(f.key as 'all' | 'error' | 'warn')}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                level === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {[
            { key: 'recent', label: 'Plus récents', icon: 'ri-time-line' },
            { key: 'frequent', label: 'Plus fréquents', icon: 'ri-repeat-line' },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setSortMode(s.key as SortMode)}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                sortMode === s.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${s.icon} text-[11px]`}></i>
              {s.label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un message, une page, un utilisateur…"
            className="w-full h-9 pl-9 pr-3 rounded-full border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>

        <label className="flex items-center gap-2 text-[11px] text-foreground-600 whitespace-nowrap cursor-pointer">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} className="accent-primary-500" />
          Afficher les résolus
        </label>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-background-200/70 bg-background-50 text-[11px] font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          title="Exporter les incidents affichés au format CSV"
        >
          <i className="ri-file-excel-2-line"></i>
          Exporter CSV
          <span className="text-foreground-400">({filtered.length})</span>
        </button>

        <button
          onClick={() => {
            void fetchLogs();
            void refreshAlerts();
          }}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          title="Rafraîchir"
        >
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
        </button>
      </div>

      {/* Bruit technique écarté */}
      {noiseCount > 0 && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100/70 border border-background-200/70 text-[11px] text-foreground-500">
          <i className="ri-filter-off-line text-foreground-400"></i>
          <span>
            <strong className="text-foreground-700">{noiseCount}</strong> entrée(s) de bruit technique écartée(s) :
            balises analytics et requêtes annulées (statut 0) ne sont pas des pannes de la plateforme.
          </span>
        </div>
      )}

      {/* Liste des incidents */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{loadError}</p>
          <button onClick={fetchLogs} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors">
            Réessayer
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-shield-check-line text-4xl text-accent-400 mb-3 block"></i>
          <p className="text-sm font-medium text-foreground-700">Aucun incident sur cette période</p>
          <p className="text-xs text-foreground-500 mt-1">Tout ce qui casse dans l’application apparaîtra ici automatiquement, regroupé par erreur identique.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((incident) => {
            const cfg = LEVEL_CONFIG[incident.level] || LEVEL_CONFIG.info;
            const isOpen = expanded === incident.key;
            return (
              <div key={incident.key} className={`rounded-lg border bg-background-50 overflow-hidden ${incident.resolved ? 'border-background-200/50 opacity-70' : 'border-background-200/70'}`}>
                <div className="px-4 py-3 flex items-start gap-3">
                  <span className={`w-8 h-8 rounded-lg ${cfg.badge} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${cfg.icon} text-sm`}></i>
                  </span>

                  <button
                    onClick={() => setExpanded(isOpen ? null : incident.key)}
                    className="flex-1 min-w-0 text-left cursor-pointer"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-700 font-medium whitespace-nowrap">
                        {prettyPage(incident.page)}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${incident.occurrences > 1 ? 'bg-red-100 text-red-700' : 'bg-background-200/70 text-foreground-600'}`}>
                        ×{incident.occurrences}
                      </span>
                      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
                      <span className="text-[11px] font-semibold text-foreground-600">{cfg.label}</span>
                      {incident.category && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                          {incident.category}
                        </span>
                      )}
                      {incident.resolved && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">Résolu</span>
                      )}
                    </div>
                    <p className="text-xs text-foreground-800 break-words line-clamp-2">{incident.message}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[10px] text-foreground-400">
                      <span>dernière : {relative(incident.lastAt)}</span>
                      {incident.occurrences > 1 && <span>première : {shortDate(incident.firstAt)}</span>}
                      {incident.latest.user_label && <span className="truncate max-w-[220px]">{incident.latest.user_label}</span>}
                    </div>
                  </button>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openErrorPage(incident.latest)}
                      title="Ouvrir la page en erreur"
                      className="flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-background-200/70 bg-background-50 text-[11px] font-medium text-foreground-700 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line"></i>
                      <span className="hidden md:inline">Ouvrir la page</span>
                    </button>
                    <button
                      onClick={() => setExpanded(isOpen ? null : incident.key)}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer"
                      title={isOpen ? 'Réduire' : 'Détails'}
                    >
                      <i className={isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-background-200/70 bg-background-100/30 px-4 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Page concernée</p>
                        <p className="text-xs text-foreground-800">{prettyPage(incident.page)}</p>
                        <p className="text-[11px] text-foreground-500 font-mono break-all">{incident.page}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Occurrences</p>
                        <p className="text-xs text-foreground-800">{incident.occurrences} fois</p>
                        <p className="text-[11px] text-foreground-500">
                          {shortDate(incident.firstAt)} → {shortDate(incident.lastAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Dernier utilisateur</p>
                        <p className="text-xs text-foreground-800 break-all">{incident.latest.user_label || 'Visiteur non connecté'}</p>
                        <p className="text-[11px] text-foreground-500 font-mono break-all">{incident.latest.user_id || '—'}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Message complet</p>
                      <pre className="whitespace-pre-wrap break-words rounded-md bg-background-50 border border-background-200/70 p-3 text-[11px] text-red-600 font-mono">{incident.message}</pre>
                    </div>

                    <div className="mb-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Contexte technique (dernière occurrence)</p>
                      <pre className="whitespace-pre-wrap break-words rounded-md bg-background-50 border border-background-200/70 p-3 text-[11px] text-foreground-700 font-mono max-h-64 overflow-auto">
{JSON.stringify(incident.latest.details || {}, null, 2)}
                      </pre>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-[11px] text-foreground-500">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">URL exacte</p>
                          <p className="font-mono break-all">{incident.latest.url || '—'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Navigateur</p>
                          <p className="break-all">{incident.latest.user_agent || '—'}</p>
                        </div>
                      </div>
                    </div>

                    {incident.logs.length > 1 && (
                      <div className="mb-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">
                          Dernières occurrences ({incident.logs.length})
                        </p>
                        <div className="rounded-md bg-background-50 border border-background-200/70 divide-y divide-background-200/60 max-h-64 overflow-auto">
                          {incident.logs.slice(0, 12).map((log) => (
                            <div key={log.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[10px]">
                              <span className="text-foreground-500 whitespace-nowrap">{shortDate(log.created_at)}</span>
                              <span className="text-foreground-600 truncate flex-1 min-w-0">{log.user_label || 'visiteur'}</span>
                              <button
                                onClick={() => openErrorPage(log)}
                                className="text-foreground-400 hover:text-primary-700 transition-colors cursor-pointer"
                                title="Ouvrir l'URL exacte"
                              >
                                <i className="ri-external-link-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openErrorPage(incident.latest)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line"></i>Ouvrir la page en erreur
                      </button>
                      <button
                        onClick={() => toggleResolved(incident)}
                        disabled={working === incident.key}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap ${
                          incident.resolved
                            ? 'bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/60'
                            : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                        }`}
                      >
                        <i className={incident.resolved ? 'ri-arrow-go-back-line' : 'ri-check-line'}></i>
                        {incident.resolved ? 'Rouvrir l’incident' : 'Marquer résolu'}
                      </button>
                      <button
                        onClick={() => copyDetails(incident)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-copy-line"></i>Copier le détail
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}